import { useState, useEffect, useMemo } from 'react';
import TablesService from '../../../../../../../../AppSettings/services/TablesService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faDownload } from '@fortawesome/free-solid-svg-icons';
import { formatTimestamp } from '../../../../../../../../utils/timestampUtils';
import './CustomTables.css';
import useActiveAccount from '../../../../../../../../AppSettings/hooks/useActiveAccount';

// Column type constants (matching Eng_templates)
const COLUMN_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  TEXTAREA: 'textarea',
  CHECKBOX: 'checkbox',
  CHECKLIST: 'checklist',
  CELL_CHECKLIST: 'cell_checklist',
};

const CustomTableView = ({ table, onRowsSaved }) => {
  const columns = useMemo(() => {
    let cols = table.columns || [];
    if (typeof cols === 'string') {
      try {
        cols = JSON.parse(cols);
      } catch {
        cols = [];
      }
    }
    return cols;
  }, [table.columns]);

  const initialRows = useMemo(() => {
    let rows = table.rows || [];
    if (typeof rows === 'string') {
      try {
        rows = JSON.parse(rows);
      } catch {
        rows = [];
      }
    }
    return rows;
  }, [table.rows]);

  // Add Last Edited and Edited By columns if not present
  const extendedColumns = [
    ...columns,
    { id: 'time_edited', label: 'Last Edited' },
    { id: 'edited_by', label: 'Edited By' },
  ];

  const [editing, setEditing] = useState({ row: null, col: null });
  const [rows, setRows] = useState(initialRows);
  const [isExporting, setIsExporting] = useState(false);
  const account = useActiveAccount();

  // Reset rows and editing state when table changes
  useEffect(() => {
    setRows(initialRows);
    setEditing({ row: null, col: null });
  }, [table.id, initialRows]);

  const handleDoubleClick = (rowIdx, colId) => {
    // Prevent editing Last Edited and Edited By columns
    if (colId === 'time_edited' || colId === 'edited_by') return;

    // Find the column to check its type
    const column = columns.find((col) => col.id === colId);

    // Don't allow double-click editing for checkbox and checklist columns
    if (
      column &&
      (column.type === COLUMN_TYPES.CHECKBOX ||
        column.type === COLUMN_TYPES.CHECKLIST)
    ) {
      return;
    }

    // For cell-specific checklist columns, only allow editing if no custom options
    if (column && column.type === COLUMN_TYPES.CELL_CHECKLIST) {
      const cellChecklistOptions = rows[rowIdx]?.[`${colId}_checklistOptions`];
      if (cellChecklistOptions && cellChecklistOptions.length > 0) {
        return; // Don't allow editing if cell has custom options
      }
      // Allow editing if no custom options (will be text field)
    }

    // Don't edit readonly columns
    if (column && column.readonly) {
      return;
    }

    setEditing({ row: rowIdx, col: colId });
  };

  const handleChange = (e, rowIdx, colId) => {
    const updatedRows = [...rows];
    updatedRows[rowIdx] = { ...updatedRows[rowIdx], [colId]: e.target.value };
    setRows(updatedRows);
  };

  // Persist the full rows array and notify the parent so switching between
  // tables in the banner reflects the latest saved data without a refresh.
  const saveRows = async (updatedRows) => {
    try {
      await TablesService.replaceAllRowsInCustomTableHolding(
        table.id,
        updatedRows
      );
      if (onRowsSaved) onRowsSaved(table.id, updatedRows);
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes.');
    }
  };

  // Handle checkbox value changes
  const handleCheckboxChange = async (rowIdx, colId, checked) => {
    const updatedRows = [...rows];
    updatedRows[rowIdx] = {
      ...updatedRows[rowIdx],
      [colId]: checked,
      time_edited: new Date().toISOString(),
      edited_by: getCurrentUserName(),
    };
    setRows(updatedRows);

    await saveRows(updatedRows);
  };

  // Handle checklist item toggle
  const handleChecklistItemToggle = async (rowIdx, colId, optionIndex) => {
    const updatedRows = [...rows];
    const currentValue = updatedRows[rowIdx][colId] || [];
    const newValue = [...currentValue];

    // Ensure array is long enough
    while (newValue.length <= optionIndex) {
      newValue.push(false);
    }

    // Toggle the specific option
    newValue[optionIndex] = !newValue[optionIndex];

    updatedRows[rowIdx] = {
      ...updatedRows[rowIdx],
      [colId]: newValue,
      time_edited: new Date().toISOString(),
      edited_by: getCurrentUserName(),
    };
    setRows(updatedRows);

    await saveRows(updatedRows);
  };

  // Handle cell-specific checklist text toggle (saves as comma-separated string)
  const handleCellChecklistTextToggle = async (rowIdx, colId, option) => {
    const updatedRows = [...rows];
    const currentValue = updatedRows[rowIdx][colId] || '';
    const selectedOptions = currentValue
      ? currentValue.split(',').map((opt) => opt.trim())
      : [];

    let newValue;
    if (selectedOptions.includes(option)) {
      // Remove option from selected list
      newValue = selectedOptions.filter((opt) => opt !== option).join(', ');
    } else {
      // Add option to selected list
      newValue = [...selectedOptions, option].join(', ');
    }

    updatedRows[rowIdx] = {
      ...updatedRows[rowIdx],
      [colId]: newValue,
      time_edited: new Date().toISOString(),
      edited_by: getCurrentUserName(),
    };
    setRows(updatedRows);

    await saveRows(updatedRows);
  };

  const handleBlur = async (rowIdx) => {
    setEditing({ row: null, col: null });
    const updatedRows = [...rows];
    // Set time_edited and edited_by for the edited row
    updatedRows[rowIdx] = {
      ...updatedRows[rowIdx],
      time_edited: new Date().toISOString(),
      edited_by: getCurrentUserName(),
    };
    setRows(updatedRows);
    await saveRows(updatedRows);
  };

  // Helper to get current user name from MSAL accounts
  function getCurrentUserName() {
    if (!account) {
      return 'Unknown User';
    }

    return (
      account.name ||
      account.username ||
      (account.idTokenClaims && account.idTokenClaims.name) ||
      (account.idTokenClaims && account.idTokenClaims.email) ||
      (account.idTokenClaims && account.idTokenClaims.oid) ||
      'Unknown User'
    );
  }

  // Handle export to Excel
  const handleExport = async () => {
    if (!table || !table.id) {
      alert('Cannot export: Table data is missing');
      return;
    }

    setIsExporting(true);
    try {
      const blob = await TablesService.exportCustomTableToExcel(table.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeTableName = (table.table_name || 'custom_table').replace(/[^a-z0-9]/gi, '_');
      a.download = `${safeTableName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting table:', error);
      alert('Failed to export table to Excel. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      {/* Export Button Toolbar */}
      <div className="custom-table-view-toolbar">
        <button
          className="custom-table-export-button"
          onClick={handleExport}
          disabled={isExporting}
          title="Export to Excel"
        >
          <FontAwesomeIcon icon={faDownload} />
          {isExporting ? 'Exporting...' : 'Export to Excel'}
        </button>
      </div>
      <div className="MD-Task-table-container">
        <table className="MD-Task-table">
        <thead>
          <tr>
            {extendedColumns.map((col) => {
              // Check if column is readonly (excluding obvious system columns)
              const isReadonly =
                col.readonly &&
                col.id !== 'time_edited' &&
                col.id !== 'edited_by';
              return (
                <th key={col.id}>
                  <div className="header-content">
                    <span>{col.label}</span>
                    {isReadonly && (
                      <FontAwesomeIcon
                        icon={faLock}
                        className="lock-icon"
                        title="Read-only column"
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {extendedColumns.map((col) => {
                const isEditing =
                  editing.row === rowIdx && editing.col === col.id;
                const isReadonly =
                  col.readonly ||
                  col.id === 'time_edited' ||
                  col.id === 'edited_by';
                const cellValue = row[col.id];

                return (
                  <td
                    key={col.id}
                    onDoubleClick={() => handleDoubleClick(rowIdx, col.id)}
                    className={`custom-table-cell ${
                      col.type === COLUMN_TYPES.CHECKBOX
                        ? 'checkbox-column'
                        : ''
                    } ${
                      col.type === COLUMN_TYPES.CHECKLIST
                        ? 'checklist-column'
                        : ''
                    } ${
                      col.type === COLUMN_TYPES.CELL_CHECKLIST
                        ? 'cell-checklist-column'
                        : ''
                    } ${isReadonly ? 'readonly' : ''}`}
                    style={{
                      cursor: isReadonly
                        ? 'not-allowed'
                        : col.type === COLUMN_TYPES.CHECKBOX ||
                          col.type === COLUMN_TYPES.CHECKLIST
                        ? 'default'
                        : col.type === COLUMN_TYPES.CELL_CHECKLIST &&
                          row[`${col.id}_checklistOptions`] &&
                          row[`${col.id}_checklistOptions`].length > 0
                        ? 'default'
                        : 'pointer',
                    }}
                  >
                    {col.type === COLUMN_TYPES.CHECKLIST ? (
                      <div className="checklist-cell">
                        {(col.checklistOptions || []).map(
                          (option, optionIndex) => {
                            const checklistValue = cellValue || [];
                            const isChecked =
                              checklistValue[optionIndex] === true;
                            return (
                              <div key={optionIndex} className="checklist-item">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() =>
                                    handleChecklistItemToggle(
                                      rowIdx,
                                      col.id,
                                      optionIndex
                                    )
                                  }
                                  disabled={isReadonly}
                                  className="custom-checklist-checkbox"
                                />
                                <span className="checklist-item-label">
                                  {option || `Option ${optionIndex + 1}`}
                                </span>
                              </div>
                            );
                          }
                        )}
                        {(!col.checklistOptions ||
                          col.checklistOptions.length === 0) && (
                          <div className="checklist-empty">
                            {/* Show blank box instead of text */}
                          </div>
                        )}
                      </div>
                    ) : col.type === COLUMN_TYPES.CELL_CHECKLIST ? (
                      <div className="cell-checklist-cell">
                        {(() => {
                          // For cell-specific checklist columns, check if this cell has its own options
                          const cellChecklistOptions =
                            row[`${col.id}_checklistOptions`];

                          if (
                            cellChecklistOptions &&
                            cellChecklistOptions.length > 0
                          ) {
                            // Cell has custom options - render them as checkboxes
                            const selectedOptions = cellValue
                              ? cellValue.split(',').map((opt) => opt.trim())
                              : [];

                            return cellChecklistOptions.map(
                              (option, optionIndex) => {
                                const isChecked =
                                  selectedOptions.includes(option);
                                return (
                                  <div
                                    key={optionIndex}
                                    className="checklist-item"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() =>
                                        handleCellChecklistTextToggle(
                                          rowIdx,
                                          col.id,
                                          option
                                        )
                                      }
                                      disabled={isReadonly}
                                      className="custom-checklist-checkbox"
                                    />
                                    <span className="checklist-item-label">
                                      {option || `Option ${optionIndex + 1}`}
                                    </span>
                                  </div>
                                );
                              }
                            );
                          } else if (isEditing) {
                            // Cell has no custom options and is being edited - show input field
                            return (
                              <input
                                type="text"
                                value={
                                  cellValue === undefined || cellValue === null
                                    ? ''
                                    : cellValue
                                }
                                onChange={(e) =>
                                  handleChange(e, rowIdx, col.id)
                                }
                                onBlur={() => handleBlur(rowIdx)}
                                autoFocus
                                className="custom-cell-input"
                              />
                            );
                          } else {
                            // Cell has no custom options and not editing - show clickable content
                            return (
                              <div
                                className="cell-content"
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  handleDoubleClick(rowIdx, col.id);
                                }}
                              >
                                {cellValue || ''}
                              </div>
                            );
                          }
                        })()}
                      </div>
                    ) : col.type === COLUMN_TYPES.CHECKBOX ? (
                      <div className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={cellValue === true || cellValue === 'true'}
                          onChange={(e) =>
                            handleCheckboxChange(
                              rowIdx,
                              col.id,
                              e.target.checked
                            )
                          }
                          disabled={isReadonly}
                          className="custom-checkbox-input"
                        />
                        {col.checkboxLabel && (
                          <span className="checkbox-label-text">
                            {col.checkboxLabel}
                          </span>
                        )}
                      </div>
                    ) : isEditing ? (
                      <input
                        type={
                          col.type === COLUMN_TYPES.NUMBER
                            ? 'number'
                            : col.type === COLUMN_TYPES.DATE
                            ? 'date'
                            : 'text'
                        }
                        value={
                          cellValue === undefined || cellValue === null
                            ? ''
                            : cellValue
                        }
                        onChange={(e) => handleChange(e, rowIdx, col.id)}
                        onBlur={() => handleBlur(rowIdx)}
                        autoFocus
                        className="custom-cell-input"
                      />
                    ) : (
                      <span className="cell-value">
                        {col.id === 'time_edited'
                          ? formatTimestamp(cellValue)
                          : cellValue === undefined || cellValue === null
                          ? ''
                          : cellValue}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default CustomTableView;
